import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Box,
  Switch,
  Button,
  TextField,
  Container,
  Alert,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme, darken, alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import {
  fetchAdminAllTenants,
  updateAdminTenant,
  deleteAdminTenant,
  fetchAgencies,
  createAgency,
  deleteAgency,
} from "../api/apiClient";
import { useTenant } from "../contexts/TenantContext";
import NewTenantWizard from "../components/NewTenantWizard";

// ── Design tokens ─────────────────────────────────────────────────────────────
const DS = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-surface-press-light)",
  surface: "#FFFFFF",
  surface2: "var(--color-surface-2)",
  surface3: "var(--color-surface-press-light)",
  danger: "#B42318",
  danger10: "rgba(180,35,24,.08)",
  danger15: "rgba(180,35,24,.14)",
};

function makeThemeColors(theme) {
  const p = theme.palette.primary.main;
  const s = theme.palette.secondary.main;
  const a = theme.palette.warning.main;
  return {
    primary: p,
    primary700: darken(p, 0.3),
    primary10: alpha(p, 0.06),
    primary15: alpha(p, 0.1),
    primaryContainer: theme.palette.primary.dark,
    green: s,
    green10: alpha(s, 0.1),
    amber: a,
    amber10: alpha(a, 0.1),
  };
}

// ── Feature definitions ───────────────────────────────────────────────────────
const FEATURE_DEFS = [
  {
    key: "classes",
    label: "Classes",
    description:
      "Enable course and class management. Coordinators can create training courses, manage schedules, assign instructors, and track enrollment.",
  },
  {
    key: "agencyManagement",
    label: "Agency Management",
    description:
      "Allow agency admins to manage their departments, track member compliance, upload COI documents, and define role requirements.",
  },
  {
    key: "equipmentManagement",
    label: "Equipment Management",
    description:
      "Track equipment (AEDs, gear, etc.) across agencies and departments within this tenant.",
  },
  {
    key: "certManagement",
    label: "Certification Management",
    description:
      "Track student certifications, expiration dates, and compliance requirements against agency role standards.",
  },
  {
    key: "certificationTrack",
    label: "Certification Track",
    description:
      "Show certification track progress and milestones on the student dashboard. Not fully implemented yet - leave off.",
  },
  {
    key: "instructorClassContent",
    label: "Instructor Class Content",
    description:
      "Allow instructors to upload and manage skill sheets, tests, and documents directly to their assigned classes.",
  },
  {
    key: "publicClassListing",
    label: "Public Class Listing",
    description:
      "Show available classes on the public-facing page without requiring login, so visitors can browse enrollment and course details.",
  },
  {
    key: "semesterPlanning",
    label: "Semester Planning",
    description:
      "Allow coordinators to organize classes into semester plans, set draft and published states, and manage the academic calendar.",
  },
];

const COLOR_FIELDS = [
  { key: "primary", label: "Primary Color", placeholder: "var(--color-primary)" },
  { key: "secondary", label: "Secondary Color", placeholder: "#15803D" },
  { key: "accent", label: "Accent Color", placeholder: "#B5651D" },
];

// ── Shared section card ───────────────────────────────────────────────────────
function SectionCard({ title, subtitle, headerAction, disabled, children }) {
  return (
    <Box
      sx={{
        bgcolor: DS.surface,
        border: `1.5px solid ${disabled ? DS.line2 : DS.line}`,
        borderRadius: "16px",
        overflow: "hidden",
        mb: 3,
        transition: "opacity .2s",
        ...(disabled ? { opacity: 0.55 } : {}),
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${DS.line2}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: DS.ink, fontFamily: "var(--font-display)" }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 12.5, color: DS.ink3, mt: "2px" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {headerAction}
      </Box>
      <Box sx={{ p: 3, pointerEvents: disabled ? "none" : "auto" }}>
        {children}
      </Box>
    </Box>
  );
}

// ── Save button with inline status ────────────────────────────────────────────
function SaveButton({ status, onClick, label = "Save" }) {
  const TC = makeThemeColors(useTheme());
  const saving = status === "saving";
  const saved = status === "saved";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {saved && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: TC.green,
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Saved</Typography>
        </Box>
      )}
      {status === "error" && (
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: DS.danger }}>
          Error saving
        </Typography>
      )}
      <Button
        onClick={onClick}
        disabled={saving}
        sx={{
          height: 34,
          px: 2.5,
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 700,
          fontSize: 13,
          bgcolor: TC.primary,
          color: "#fff",
          boxShadow: "none",
          "&:hover": { bgcolor: TC.primary700, boxShadow: "none" },
          "&.Mui-disabled": { bgcolor: DS.line2, color: DS.ink4 },
        }}
      >
        {saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : label}
      </Button>
    </Box>
  );
}

// ── Custom profile fields editor ──────────────────────────────────────────────
function CustomFieldEditor({ fields = [], onChange }) {
  const TC = makeThemeColors(useTheme());
  const [newField, setNewField] = useState("");

  const add = () => {
    const trimmed = newField.trim();
    if (!trimmed) return;
    onChange([...fields, trimmed]);
    setNewField("");
  };

  return (
    <Box>
      {fields.map((f, i) => (
        <Box key={i} sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            value={f}
            size="small"
            fullWidth
            onChange={(e) => {
              const next = [...fields];
              next[i] = e.target.value;
              onChange(next);
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
          <IconButton
            size="small"
            onClick={() => onChange(fields.filter((_, j) => j !== i))}
            sx={{
              color: DS.danger,
              border: `1.5px solid ${DS.line}`,
              borderRadius: "8px",
              width: 34,
              height: 34,
            }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <TextField
          placeholder="Field name (e.g. Badge Number)"
          size="small"
          value={newField}
          onChange={(e) => setNewField(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          fullWidth
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />
        <Button
          onClick={add}
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          sx={{
            height: 40,
            px: 2,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 13,
            bgcolor: TC.primary10,
            color: TC.primary,
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: TC.primary15 },
          }}
        >
          Add Field
        </Button>
      </Box>
    </Box>
  );
}

// ── Agency editor ─────────────────────────────────────────────────────────────
function AgencyEditor({ tenantId, agencies, setAgencies }) {
  const TC = makeThemeColors(useTheme());
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  const add = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setAdding(true);
    try {
      const a = await createAgency(tenantId, { name: trimmed });
      setAgencies([...agencies, a]);
      setNewName("");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (idx) => {
    const a = agencies[idx];
    await deleteAgency(tenantId, a.id);
    setAgencies(agencies.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      {agencies.length === 0 && (
        <Typography sx={{ fontSize: 13, color: DS.ink3, mb: 2 }}>
          No agencies yet. Add one below.
        </Typography>
      )}
      {agencies.map((a, i) => (
        <Box
          key={a.id}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 1.5,
            p: "10px 14px",
            bgcolor: DS.surface2,
            borderRadius: "10px",
            border: `1.5px solid ${DS.line2}`,
          }}
        >
          <BusinessIcon sx={{ fontSize: 18, color: DS.ink4, flexShrink: 0 }} />
          <Typography
            sx={{ fontSize: 13.5, fontWeight: 600, color: DS.ink, flex: 1 }}
          >
            {a.name}
          </Typography>
          <Chip
            label={`ID: ${a.id}`}
            size="small"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              bgcolor: TC.primary10,
              color: TC.primary,
            }}
          />
          <IconButton
            size="small"
            onClick={() => remove(i)}
            sx={{ color: DS.danger, ml: 0.5 }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        <TextField
          placeholder="Agency name (e.g. Central Fire Department)"
          size="small"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          fullWidth
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />
        <Button
          onClick={add}
          disabled={adding}
          startIcon={
            adding ? (
              <CircularProgress size={14} />
            ) : (
              <AddIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            height: 40,
            px: 2,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 13,
            bgcolor: TC.primary10,
            color: TC.primary,
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: TC.primary15 },
          }}
        >
          Add Agency
        </Button>
      </Box>
    </Box>
  );
}

// ── Hub: tenant tile ───────────────────────────────────────────────────────────
function TenantTile({ tenant, onOpen }) {
  const TC = makeThemeColors(useTheme());
  const features = tenant.features || tenant.config?.features || {};
  const featureCount = Object.values(features).filter(Boolean).length;
  const locked = !!(tenant.locked ?? tenant.config?.locked);
  const primary =
    tenant.theme?.primary || tenant.config?.theme?.primary || TC.primary;
  const domain = tenant.customDomain || tenant.config?.customDomain || "";
  const initial = (tenant.name || "?").trim()[0]?.toUpperCase() || "?";

  return (
    <Box
      onClick={onOpen}
      sx={{
        bgcolor: DS.surface,
        border: `1px solid ${DS.line}`,
        borderRadius: "20px",
        boxShadow: "0 1px 3px rgba(20,24,40,.06)",
        overflow: "hidden",
        cursor: "pointer",
        transition: "box-shadow .16s, border-color .16s, transform .16s",
        "&:hover": {
          boxShadow: "0 8px 24px -10px rgba(20,24,40,.15)",
          borderColor: "var(--color-hairline-cool)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box sx={{ height: 4, bgcolor: primary }} />
      <Box sx={{ p: "20px 22px 18px" }}>
        <Box
          sx={{ display: "flex", alignItems: "flex-start", gap: "13px", mb: 2 }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: primary,
              display: "grid",
              placeItems: "center",
              color: "#fff",
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: "-.01em",
              flexShrink: 0,
            }}
          >
            {initial}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pt: "2px" }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 800,
                color: DS.ink,
                letterSpacing: "-.02em",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontFamily: "var(--font-display)",
              }}
            >
              {tenant.name}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: DS.ink4,
                fontFamily: domain ? "monospace" : "inherit",
                fontStyle: domain ? "normal" : "italic",
                mt: "3px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {domain || "No custom domain"}
            </Typography>
          </Box>
          <Chip
            icon={locked ? <LockIcon sx={{ fontSize: 11 }} /> : undefined}
            label={locked ? "Locked" : "Active"}
            size="small"
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              flexShrink: 0,
              bgcolor: locked ? TC.amber10 : TC.green10,
              color: locked ? TC.amber : TC.green,
              border: `1px solid ${locked ? alpha(TC.amber, 0.22) : alpha(TC.green, 0.20)}`,
            }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 800,
                color: DS.ink,
                letterSpacing: "-.02em",
                lineHeight: 1,
                fontFamily: "var(--font-display)",
              }}
            >
              {featureCount}
            </Typography>
            <Typography
              sx={{ fontSize: 10.5, color: DS.ink4, fontWeight: 500 }}
            >
              Features enabled
            </Typography>
          </Box>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            endIcon={
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            }
            sx={{
              height: 34,
              px: "14px",
              borderRadius: "9px",
              border: `1.5px solid ${DS.line}`,
              bgcolor: DS.surface2,
              fontSize: 12.5,
              fontWeight: 700,
              color: DS.ink3,
              textTransform: "none",
              "&:hover": {
                bgcolor: TC.primary10,
                color: TC.primary,
                borderColor: TC.primary15,
              },
            }}
          >
            Edit Settings
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ── Hub: new tenant tile ───────────────────────────────────────────────────────
function NewTenantTile({ onClick, TC }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        border: `1.5px dashed ${DS.line}`,
        borderRadius: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "11px",
        p: "44px 24px",
        textAlign: "center",
        minHeight: 220,
        cursor: "pointer",
        transition: "all .16s",
        "&:hover": {
          bgcolor: "#fff",
          borderColor: TC.primary,
          borderStyle: "solid",
          boxShadow: "0 1px 3px rgba(20,24,40,.06)",
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "14px",
          bgcolor: DS.surface3,
          display: "grid",
          placeItems: "center",
          color: DS.ink4,
        }}
      >
        <AddIcon sx={{ fontSize: 22 }} />
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: DS.ink3, fontFamily: "var(--font-display)" }}>
        Set up a new tenant
      </Typography>
      <Typography
        sx={{ fontSize: 12, color: DS.ink4, lineHeight: 1.5, maxWidth: 200 }}
      >
        Walk through a guided setup to configure identity, features, and
        branding
      </Typography>
    </Box>
  );
}

// ── Edit view: sidebar nav ────────────────────────────────────────────────────
const EDIT_TABS = [
  { key: "details", label: "Details" },
  { key: "features", label: "Features" },
  { key: "branding", label: "Branding" },
  { key: "agencies", label: "Agencies" },
];

function EditSidebar({
  tenant,
  activeTab,
  setActiveTab,
  onBack,
  locked,
  lockLoading,
  onLockClick,
  onUnlock,
  onDeleteClick,
}) {
  const TC = makeThemeColors(useTheme());
  const primary =
    tenant.theme?.primary || tenant.config?.theme?.primary || TC.primary;
  const initial = (tenant.name || "?").trim()[0]?.toUpperCase() || "?";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        position: { md: "sticky" },
        top: { md: 88 },
      }}
    >
      <Button
        onClick={onBack}
        startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
        sx={{
          justifyContent: "flex-start",
          color: DS.ink4,
          textTransform: "none",
          fontWeight: 600,
          fontSize: 13,
          mb: "6px",
          borderRadius: "9px",
          "&:hover": { bgcolor: DS.surface3, color: DS.ink },
        }}
      >
        All Tenants
      </Button>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          p: "10px 12px",
          bgcolor: "#fff",
          border: `1px solid ${DS.line2}`,
          borderRadius: "12px",
          mb: "10px",
          boxShadow: "0 1px 3px rgba(20,24,40,.06)",
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            bgcolor: primary,
            display: "grid",
            placeItems: "center",
            fontSize: 11,
            fontWeight: 800,
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {initial}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 700,
              color: DS.ink,
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {tenant.name}
          </Typography>
          <Typography sx={{ fontSize: 11, color: DS.ink4, mt: "1px" }}>
            Tenant ID: {tenant.id}
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".1em",
          textTransform: "uppercase",
          color: DS.ink4,
          px: "10px",
          pt: "10px",
          pb: "4px",
        }}
      >
        Configure
      </Typography>
      {EDIT_TABS.map((t) => (
        <Button
          key={t.key}
          onClick={() => setActiveTab(t.key)}
          sx={{
            justifyContent: "flex-start",
            px: "12px",
            py: "9px",
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13.5,
            color: activeTab === t.key ? TC.primary : DS.ink3,
            bgcolor: activeTab === t.key ? TC.primary10 : "transparent",
            "&:hover": {
              bgcolor: activeTab === t.key ? TC.primary10 : DS.surface3,
              color: activeTab === t.key ? TC.primary : DS.ink,
            },
          }}
        >
          {t.label}
        </Button>
      ))}

      <Divider sx={{ my: "6px", mx: "10px" }} />
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".1em",
          textTransform: "uppercase",
          color: DS.ink4,
          px: "10px",
          pb: "4px",
        }}
      >
        Actions
      </Typography>
      <Button
        onClick={locked ? onUnlock : onLockClick}
        disabled={lockLoading}
        startIcon={
          lockLoading ? (
            <CircularProgress size={14} />
          ) : locked ? (
            <LockOpenIcon sx={{ fontSize: 15 }} />
          ) : (
            <LockIcon sx={{ fontSize: 15 }} />
          )
        }
        sx={{
          justifyContent: "flex-start",
          px: "12px",
          py: "9px",
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: 13.5,
          color: TC.amber,
          "&:hover": { bgcolor: TC.amber10 },
        }}
      >
        {locked ? "Unlock Tenant" : "Lock Tenant"}
      </Button>
      <Button
        onClick={onDeleteClick}
        startIcon={<DeleteIcon sx={{ fontSize: 15 }} />}
        sx={{
          justifyContent: "flex-start",
          px: "12px",
          py: "9px",
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: 13.5,
          color: DS.danger,
          "&:hover": { bgcolor: DS.danger10 },
        }}
      >
        Delete Tenant
      </Button>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function TenantSetupPage() {
  const TC = makeThemeColors(useTheme());
  const { user } = useTenant();

  const [view, setView] = useState("hub"); // 'hub' | 'wizard' | 'edit'
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [agenciesData, setAgenciesData] = useState({});

  // Section forms
  const [detailsForm, setDetailsForm] = useState({
    name: "",
    customDomain: "",
    customFields: [],
  });
  const [featuresForm, setFeaturesForm] = useState({});
  const [themeForm, setThemeForm] = useState({ logo: "", icon: "", theme: {} });

  // Save status per section: null | 'saving' | 'saved' | 'error'
  const [detailsStatus, setDetailsStatus] = useState(null);
  const [themeStatus, setThemeStatus] = useState(null);
  const [lockLoading, setLockLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const isAdmin =
    user?.roles?.includes("GLOBAL_ADMIN") || user?.roles?.includes("ADMIN");

  // ── Load tenants on mount ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    fetchAdminAllTenants().then((list) => {
      setTenants(list || []);
    });
  }, [isAdmin]);

  // ── Sync forms when selected tenant changes ───────────────────────────────
  useEffect(() => {
    if (!selected) return;
    setDetailsForm({
      name: selected.name || selected.config?.name || "",
      customDomain:
        selected.customDomain || selected.config?.customDomain || "",
      customFields:
        selected.customFields || selected.config?.customFields || [],
    });
    setFeaturesForm(selected.features || selected.config?.features || {});
    setThemeForm({
      logo: selected.logo || selected.config?.logo || "",
      icon: selected.icon || selected.config?.icon || "",
      theme: selected.theme || selected.config?.theme || {},
    });
    setDetailsStatus(null);
    setThemeStatus(null);
    // Load agencies for this tenant
    fetchAgencies(String(selected.id))
      .then((a) =>
        setAgenciesData((prev) => ({ ...prev, [selected.id]: a || [] })),
      )
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const patchSelected = useCallback(
    (updates) => {
      const updated = { ...selected, ...updates };
      setSelected(updated);
      setTenants((prev) =>
        prev.map((t) => (t.id === updated.id ? updated : t)),
      );
    },
    [selected],
  );

  const openEdit = (tenant) => {
    setSelected(tenant);
    setActiveTab("details");
    setView("edit");
  };

  const handleWizardComplete = (created) => {
    setTenants((prev) => [...prev, created]);
    setSelected(created);
    setActiveTab("details");
    setView("edit");
  };

  // ── Save details ──────────────────────────────────────────────────────────
  const saveDetails = async () => {
    setDetailsStatus("saving");
    try {
      const result = await updateAdminTenant(selected.id, {
        name: detailsForm.name,
        customDomain: detailsForm.customDomain || null,
        customFields: detailsForm.customFields,
      });
      patchSelected(result);
      setDetailsStatus("saved");
      setTimeout(() => setDetailsStatus(null), 2500);
    } catch {
      setDetailsStatus("error");
    }
  };

  // ── Toggle feature (auto-saves immediately) ────────────────────────────────
  const toggleFeature = async (key, val) => {
    const next = { ...featuresForm, [key]: val };
    setFeaturesForm(next);
    try {
      const result = await updateAdminTenant(selected.id, { features: next });
      patchSelected(result);
    } catch {
      setFeaturesForm(featuresForm); // revert on error
    }
  };

  // ── Save theme ────────────────────────────────────────────────────────────
  const saveTheme = async () => {
    setThemeStatus("saving");
    try {
      const result = await updateAdminTenant(selected.id, {
        logo: themeForm.logo || null,
        icon: themeForm.icon || null,
        theme: themeForm.theme,
      });
      patchSelected(result);
      setThemeStatus("saved");
      setTimeout(() => setThemeStatus(null), 2500);
    } catch {
      setThemeStatus("error");
    }
  };

  // ── Lock / unlock ─────────────────────────────────────────────────────────
  const doToggleLock = async (nextLocked) => {
    setLockLoading(true);
    try {
      const result = await updateAdminTenant(selected.id, {
        locked: nextLocked,
      });
      patchSelected({ locked: result.locked ?? nextLocked });
    } finally {
      setLockLoading(false);
    }
  };

  const confirmLock = async () => {
    await doToggleLock(true);
    setLockModalOpen(false);
  };

  const unlockNow = () => doToggleLock(false);

  // ── Delete ────────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await deleteAdminTenant(selected.id);
      const remaining = tenants.filter((t) => t.id !== selected.id);
      setTenants(remaining);
      setDeleteModalOpen(false);
      setSelected(null);
      setView("hub");
    } catch (e) {
      setDeleteError(e?.response?.data?.message || "Failed to delete tenant");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">
          You do not have permission to view this page.
        </Alert>
      </Container>
    );
  }

  const locked = !!(selected?.locked ?? selected?.config?.locked);
  const agencyEnabled = !!featuresForm.agencyManagement;

  // ── VIEW: WIZARD ──────────────────────────────────────────────────────────
  if (view === "wizard") {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <NewTenantWizard
          onComplete={handleWizardComplete}
          onCancel={() => setView("hub")}
        />
      </Container>
    );
  }

  // ── VIEW: EDIT ────────────────────────────────────────────────────────────
  if (view === "edit" && selected) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            maxWidth: 1060,
            mx: "auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "224px 1fr" },
            gap: "24px",
            alignItems: "start",
          }}
        >
          <EditSidebar
            tenant={selected}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onBack={() => setView("hub")}
            locked={locked}
            lockLoading={lockLoading}
            onLockClick={() => setLockModalOpen(true)}
            onUnlock={unlockNow}
            onDeleteClick={() => setDeleteModalOpen(true)}
          />

          <Box sx={{ minWidth: 0 }}>
            {activeTab === "details" && (
              <>
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: 800,
                      letterSpacing: "-.03em",
                      color: DS.ink,
                      fontFamily: "var(--font-display)",
                    }}
                  >
                    Tenant Details
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13.5, color: DS.ink3, mt: "4px" }}
                  >
                    Identity, domain, and custom profile fields
                  </Typography>
                </Box>
                <SectionCard
                  title="Identity"
                  subtitle="Name and public-facing domain"
                  headerAction={
                    <SaveButton
                      status={detailsStatus}
                      onClick={saveDetails}
                      label="Save Details"
                    />
                  }
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="Tenant Name"
                      value={detailsForm.name}
                      onChange={(e) =>
                        setDetailsForm({ ...detailsForm, name: e.target.value })
                      }
                      size="small"
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                      }}
                    />
                    <TextField
                      label="Custom Domain URL"
                      value={detailsForm.customDomain}
                      onChange={(e) =>
                        setDetailsForm({
                          ...detailsForm,
                          customDomain: e.target.value,
                        })
                      }
                      size="small"
                      fullWidth
                      placeholder="mytrainingcenter.example.com"
                      helperText="Hostname only — no https:// or trailing slash"
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                      }}
                    />
                  </Box>
                </SectionCard>
                <SectionCard
                  title="Custom Profile Fields"
                  subtitle="Extra fields shown on user profiles"
                >
                  <CustomFieldEditor
                    fields={detailsForm.customFields}
                    onChange={(list) =>
                      setDetailsForm({ ...detailsForm, customFields: list })
                    }
                  />
                </SectionCard>
              </>
            )}

            {activeTab === "features" && (
              <>
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: 800,
                      letterSpacing: "-.03em",
                      color: DS.ink,
                      fontFamily: "var(--font-display)",
                    }}
                  >
                    Features
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13.5, color: DS.ink3, mt: "4px" }}
                  >
                    Toggle platform capabilities — changes save automatically
                  </Typography>
                </Box>
                <SectionCard
                  title="Feature Flags"
                  subtitle={`${Object.values(featuresForm).filter(Boolean).length} of ${FEATURE_DEFS.length} enabled`}
                >
                  {FEATURE_DEFS.map((feat, idx) => (
                    <React.Fragment key={feat.key}>
                      {idx > 0 && <Divider sx={{ my: 0 }} />}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          py: 2,
                        }}
                      >
                        <Switch
                          checked={!!featuresForm[feat.key]}
                          onChange={(e) =>
                            toggleFeature(feat.key, e.target.checked)
                          }
                          size="small"
                          sx={{
                            mt: "1px",
                            flexShrink: 0,
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: TC.primary,
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              { bgcolor: TC.primary },
                          }}
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: DS.ink,
                            }}
                          >
                            {feat.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: DS.ink3,
                              mt: "3px",
                              lineHeight: 1.55,
                            }}
                          >
                            {feat.description}
                          </Typography>
                        </Box>
                      </Box>
                    </React.Fragment>
                  ))}
                </SectionCard>
              </>
            )}

            {activeTab === "branding" && (
              <>
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: 800,
                      letterSpacing: "-.03em",
                      color: DS.ink,
                      fontFamily: "var(--font-display)",
                    }}
                  >
                    Branding &amp; Theming
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13.5, color: DS.ink3, mt: "4px" }}
                  >
                    Logos, icons, and color scheme for this tenant
                  </Typography>
                </Box>
                <SectionCard
                  title="Logo, Icon &amp; Color Scheme"
                  subtitle="Applied to the sidebar header, favicon, and accent colors"
                  headerAction={
                    <SaveButton
                      status={themeStatus}
                      onClick={saveTheme}
                      label="Save Branding"
                    />
                  }
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    {[
                      {
                        key: "logo",
                        label: "Logo URL",
                        hint: "Displayed in the sidebar header",
                      },
                      {
                        key: "icon",
                        label: "Icon URL",
                        hint: "Used as favicon and avatar",
                      },
                    ].map(({ key, label, hint }) => (
                      <Box key={key}>
                        <TextField
                          label={label}
                          value={themeForm[key]}
                          onChange={(e) =>
                            setThemeForm({
                              ...themeForm,
                              [key]: e.target.value,
                            })
                          }
                          size="small"
                          fullWidth
                          placeholder="https://example.com/image.png"
                          helperText={hint}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                          }}
                        />
                        {themeForm[key] && (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1.5,
                              bgcolor: DS.surface2,
                              borderRadius: "8px",
                              border: `1px solid ${DS.line2}`,
                              display: "inline-flex",
                            }}
                          >
                            <img
                              src={themeForm[key]}
                              alt={`${key} preview`}
                              style={{
                                maxHeight: 48,
                                maxWidth: key === "icon" ? 48 : 140,
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>

                  <Divider sx={{ my: 2.5 }} />

                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: DS.ink,
                      mb: 0.5,
                      fontFamily: "var(--font-display)",
                    }}
                  >
                    Color Scheme
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: DS.ink3, mb: 2 }}>
                    Colors are applied to the tenant's sidebar, buttons, and
                    accents.
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                      gap: 2,
                    }}
                  >
                    {COLOR_FIELDS.map(({ key, label, placeholder }) => {
                      const val = themeForm.theme?.[key] || "";
                      return (
                        <Box key={key}>
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              fontWeight: 700,
                              color: DS.ink3,
                              mb: 1,
                              textTransform: "uppercase",
                              letterSpacing: ".06em",
                            }}
                          >
                            {label}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              component="input"
                              type="color"
                              value={val || placeholder}
                              onChange={(e) =>
                                setThemeForm({
                                  ...themeForm,
                                  theme: {
                                    ...themeForm.theme,
                                    [key]: e.target.value,
                                  },
                                })
                              }
                              sx={{
                                width: 36,
                                height: 36,
                                flexShrink: 0,
                                border: `1.5px solid ${DS.line}`,
                                borderRadius: "8px",
                                cursor: "pointer",
                                p: "2px",
                                bgcolor: "transparent",
                              }}
                            />
                            <TextField
                              size="small"
                              value={val}
                              onChange={(e) =>
                                setThemeForm({
                                  ...themeForm,
                                  theme: {
                                    ...themeForm.theme,
                                    [key]: e.target.value,
                                  },
                                })
                              }
                              placeholder={placeholder}
                              sx={{
                                flex: 1,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  fontFamily: "monospace",
                                  fontSize: 13,
                                },
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              mt: 1,
                              height: 5,
                              borderRadius: "999px",
                              bgcolor: val || placeholder,
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </SectionCard>
              </>
            )}

            {activeTab === "agencies" && (
              <>
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: 800,
                      letterSpacing: "-.03em",
                      color: DS.ink,
                      fontFamily: "var(--font-display)",
                    }}
                  >
                    Agencies
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13.5, color: DS.ink3, mt: "4px" }}
                  >
                    Manage agencies and departments within this tenant
                  </Typography>
                </Box>
                <SectionCard
                  title="Agencies"
                  subtitle={
                    agencyEnabled
                      ? "Manage agencies within this tenant"
                      : "Enable Agency Management in Features to manage agencies"
                  }
                  disabled={!agencyEnabled}
                >
                  {agencyEnabled ? (
                    <AgencyEditor
                      tenantId={String(selected.id)}
                      agencies={agenciesData[selected.id] || []}
                      setAgencies={(list) =>
                        setAgenciesData((prev) => ({
                          ...prev,
                          [selected.id]: list,
                        }))
                      }
                    />
                  ) : (
                    <Typography sx={{ fontSize: 13, color: DS.ink4 }}>
                      Turn on the "Agency Management" feature to manage agencies
                      for this tenant.
                    </Typography>
                  )}
                </SectionCard>
              </>
            )}
          </Box>
        </Box>

        {/* Lock confirmation */}
        <Dialog
          open={lockModalOpen}
          onClose={() => setLockModalOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 800, fontSize: 18, fontFamily: "var(--font-display)" }}>
            Lock this tenant?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: DS.ink3, lineHeight: 1.6 }}>
              Locking will suspend access for all users of this tenant. Admins
              can unlock it at any time.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setLockModalOpen(false)}
              sx={{ textTransform: "none", fontWeight: 700, color: DS.ink3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmLock}
              disabled={lockLoading}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: TC.amber10,
                color: TC.amber,
                borderRadius: "9px",
                px: 2,
                "&:hover": { bgcolor: alpha(TC.amber, 0.18) },
              }}
            >
              {lockLoading ? <CircularProgress size={16} /> : "Lock Tenant"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 800, fontSize: 18, fontFamily: "var(--font-display)" }}>
            Delete this tenant?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: DS.ink3, lineHeight: 1.6 }}>
              This will permanently delete "{selected.name}" and all associated
              data. This cannot be undone.
            </Typography>
            {deleteError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>
                {deleteError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setDeleteModalOpen(false)}
              sx={{ textTransform: "none", fontWeight: 700, color: DS.ink3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteLoading}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: DS.danger10,
                color: DS.danger,
                borderRadius: "9px",
                px: 2,
                "&:hover": { bgcolor: DS.danger15 },
              }}
            >
              {deleteLoading ? (
                <CircularProgress size={16} />
              ) : (
                "Delete Permanently"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // ── VIEW: HUB ─────────────────────────────────────────────────────────────
  return (
    <>
      <Box sx={{ borderBottom: `1px solid ${DS.line}` }}>
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 22, md: 28 },
                  fontWeight: 800,
                  color: DS.ink,
                  letterSpacing: "-.03em",
                  lineHeight: 1.2,
                  fontFamily: "var(--font-display)",
                }}
              >
                Tenant Setup
              </Typography>
              <Typography sx={{ fontSize: 14, color: DS.ink3, mt: "4px" }}>
                Manage tenant configurations, features, agencies, and branding
              </Typography>
            </Box>
            <Button
              onClick={() => setTimeout(() => setView("wizard"), 0)}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              sx={{
                height: 40,
                px: 2.5,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                bgcolor: TC.primary,
                color: "#fff",
                boxShadow: "none",
                "&:hover": { bgcolor: TC.primary700, boxShadow: "none" },
              }}
            >
              New Tenant
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ maxWidth: 920, mx: "auto" }}>
          <Typography
            sx={{ fontSize: 12.5, color: DS.ink4, fontWeight: 500, mb: "14px" }}
          >
            {tenants.length} tenant{tenants.length !== 1 ? "s" : ""}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: "14px",
            }}
          >
            {tenants.map((t) => (
              <TenantTile key={t.id} tenant={t} onOpen={() => openEdit(t)} />
            ))}
            <NewTenantTile TC={TC} onClick={() => setTimeout(() => setView("wizard"), 0)} />
          </Box>
        </Box>
      </Container>
    </>
  );
}
